// API Response Types
export interface MinigamesServiceCampaignOutput {
  __typename?: 'MinigamesServiceCampaignOutput'
  authenticationRequired: boolean
  createdAt: string
  createdBy: string
  description?: string | null
  distributionType: string
  endDate: string
  gameConfig: any // JSON object
  id: string
  maxPlaysPerUser: number
  name: string
  playCodeRequired: boolean
  startDate: string
  status: string
  templateId: string
  tenantId: string
  uiConfig: any // JSON object
  updatedAt: string
}

export interface MinigamesServiceCampaignsOutput {
  __typename?: 'MinigamesServiceCampaignsOutput'
  data: MinigamesServiceCampaignOutput[]
  total: number
}

export interface MinigamesServiceGameTemplateOutput {
  __typename?: 'MinigamesServiceGameTemplateOutput'
  createdAt: string
  defaultGameConfig?: any | null
  defaultUiConfig?: any | null
  gameType: string // GameTypeEnum
  id: string
  isActive: boolean
  templateName: string
  tenantId: string
  updatedAt: string
}

export interface MinigamesServicePrizeOutput {
  __typename?: 'MinigamesServicePrizeOutput'
  campaignId: string
  createdAt: string
  id: string
  isActive: boolean
  originalProbability: number
  pointsAmount?: number | null
  prizeType: string
  probability: number
  productId?: string | null
  quantity: number
  remainingQuantity: number
  tenantId: string
  updatedAt: string
}

export interface MinigamesServiceCodeOutput {
  __typename?: 'MinigamesServiceCodeOutput'
  campaignId: string
  code: string
  createdAt?: string | null
  gameplayId?: string | null
  id: string
  isUsed: boolean
  tenantId: string
  updatedAt?: string | null
  usedAt?: string | null
}

export interface MinigamesServiceGameRecordOutput {
  __typename?: 'MinigamesServiceGameRecordOutput'
  campaignId: string
  codeId?: string | null
  createdAt?: string | null
  guestName?: string | null
  guestPhone?: string | null
  id: string
  playerType: string
  prizeId: string
  tenantId: string
  updatedAt?: string | null
  userId?: string | null
}

export interface MinigamesServiceQuestionOutput {
  __typename?: 'MinigamesServiceQuestionOutput'
  answers?: MinigamesServiceAnswerOutput[] | null
  audioUrl?: string | null
  campaignId: string
  content: string
  correctAnswerIds?: string[] | null
  createdAt: string
  explanation?: string | null
  id: string
  isActive: boolean
  mediaUrl?: string | null
  metadata?: any | null
  order?: number | null
  passage?: string | null
  points?: number | null
  presentationType: string // PresentationTypeEnum
  questionType: string // QuestionTypeEnum
  tenantId: string
  updatedAt: string
  videoUrl?: string | null
}

export interface MinigamesServiceAnswerOutput {
  __typename?: 'MinigamesServiceAnswerOutput'
  id: string
  text: string
  isCorrect: boolean
  order: number
  imageUrl?: string | null
  questionId: string
}

export interface MinigamesServiceQuestionsOutput {
  __typename?: 'MinigamesServiceQuestionsOutput'
  data: MinigamesServiceQuestionOutput[]
  total: number
}

// Enums
export enum GameTypeEnum {
  QUIZ = 'Quiz',
  LUCKYWHEEL = 'LuckyWheel',
  FLIPCARD = 'FlipCard',
}

export enum CampaignStatusEnum {
  ACTIVE = 'Active',
  DRAFT = 'Draft',
  PAUSED = 'Paused',
  ENDED = 'Ended',
}

export enum DistributionTypeEnum {
  INSTANT = 'Instant',
  RANK_BASED = 'RankBased',
}

export enum PrizeTypeEnum {
  PRODUCT = 'Product',
  POINTS = 'Points',
  VOUCHER = 'Voucher',
  NOTHING = 'Nothing',
}

export enum QuestionTypeEnum {
  SINGLE_CHOICE = 'SINGLE_CHOICE',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  TRUE_FALSE = 'TRUE_FALSE',
  FILL_BLANK = 'FILL_BLANK',
}

export enum PresentationTypeEnum {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  AUDIO = 'AUDIO',
}

// Input Types for mutations
export interface MinigamesServiceCreateCampaignInput {
  authenticationRequired?: boolean
  description?: string
  distributionType?: DistributionTypeEnum
  enablePrizeSystem?: boolean
  enableTierSystem?: boolean
  endDate: string
  gameConfigOverride?: any
  maxPlaysPerUser?: number
  name: string
  playCodeRequired?: boolean
  startDate: string
  templateId: string
  uiConfigOverride?: any
}

export interface MinigamesServiceCreateGameTemplateInput {
  templateName: string
  gameType: GameTypeEnum
  defaultGameConfig?: any
  defaultUiConfig?: any
}

export interface MinigamesServiceUpdateCampaignInput {
  description?: string
  enablePrizeSystem?: boolean
  enableTierSystem?: boolean
  endDate?: string
  gameConfigOverride?: any
  name?: string
  startDate?: string
  uiConfigOverride?: any
}

export interface MinigamesServiceGameRecordFilterInput {
  campaignId?: string
  endDate?: string
  guestPhone?: string
  prizeType?: PrizeTypeEnum
  startDate?: string
  userId?: string
}

// UI Component interfaces (mapped from API types)
export interface Campaign {
  id: string
  name: string
  description?: string
  status: CampaignStatusEnum
  startDate: string
  endDate: string
  gameType?: GameTypeEnum
  templateId?: string
  templateName?: string
  maxPlaysPerUser: number
  authenticationRequired: boolean
  playCodeRequired: boolean
  distributionType?: string
  gameConfig?: any
  uiConfig?: any
  thumbnailUrl?: string
  howToPlay?: string
  rules?: string
  enablePrizeSystem?: boolean
  enableTierSystem?: boolean
  createdAt: string
  updatedAt: string
  createdBy: string
  participantsCount?: number // Will be calculated from game records
}

export interface GameTemplate {
  id: string
  templateName: string
  gameType: GameTypeEnum
  isActive: boolean
  defaultGameConfig?: any
  defaultUiConfig?: any
  createdAt: string
  updatedAt: string
}
